import React from 'react';
import { BarChart, Bar, XAxis, YAxis } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip } from '@frontend/shadcn/components/ui/chart';
import { formatCurrency } from '../../../utils/formatCurrency';

interface MetricBarChartProps {
  aspireValue: number;
  nonAspireValue: number;
  metricType: 'currency' | 'percentage';
  metricName: string;
  currency?: string;
}

interface TooltipEntry {
  dataKey: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
}

export const MetricBarChart = ({
  aspireValue,
  nonAspireValue,
  metricType,
  metricName,
  currency = 'USD',
}: MetricBarChartProps) => {
  const chartData = [
    {
      name: metricName,
      aspire: aspireValue,
      nonAspire: nonAspireValue,
    },
  ];

  const chartConfig: ChartConfig = {
    aspire: {
      label: 'Attributed to Aspire',
      color: 'hsl(var(--refresh-blue-5))', // Dark blue
    },
    nonAspire: {
      label: 'Not Attributed to Aspire',
      color: 'hsl(var(--refresh-blue-2))', // Light blue
    },
  };

  const formatValue = (value: number) => {
    // Handle NaN
    if (typeof value !== 'number' || isNaN(value)) {
      return metricType === 'currency' ? '--' : '--%';
    }
    // Handle extremely large values
    if (!Number.isFinite(value) || Math.abs(value) > 1e12) {
      return metricType === 'currency' ? '> 1T' : '> 1T%';
    }
    // Handle negative values
    if (value < 0) {
      // Optionally flag or format negative values
      return metricType === 'currency'
        ? `(${formatCurrency(Math.abs(value), currency)})`
        : `(${Math.abs(value).toFixed(1)}%)`;
    }
    if (metricType === 'currency') {
      return formatCurrency(value, currency);
    } else {
      return `${value.toFixed(1)}%`;
    }
  };

  const CustomTooltipContent = ({ active, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-2">
          {payload.map((entry: TooltipEntry, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
              <span className="text-sm">
                {entry.dataKey === 'aspire' ? 'Attributed to Aspire' : 'Not Attributed to Aspire'}:{' '}
                {formatValue(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ChartContainer config={chartConfig} className="h-32 w-[116px] flex items-end justify-center bg-transparent">
      <BarChart data={chartData} width={116} height={128} barCategoryGap="20%" className="flex items-end gap-1">
        <XAxis dataKey="name" hide />
        <YAxis hide />
        <ChartTooltip cursor={false} content={<CustomTooltipContent />} />
        <Bar
          dataKey="aspire"
          fill="hsl(var(--refresh-blue-5))"
          radius={[2, 2, 0, 0]}
          className="rounded-tl-[2px] rounded-tr-[2px]"
          barSize={40}
        />
        <Bar
          dataKey="nonAspire"
          fill="hsl(var(--refresh-blue-2))"
          radius={[2, 2, 0, 0]}
          className="rounded-tl-[2px] rounded-tr-[2px]"
          barSize={40}
        />
      </BarChart>
    </ChartContainer>
  );
};
